import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';

const AddEditFlight = () => {
  const { flightId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(flightId);

  const [formData, setFormData] = useState({
    flight_number: '',
    title: '',
    date: '',
    departure_time: '',
    arrival_time: '',
    duration: '',
    trip_type: 'Aller simple',
    return_date: '',
    return_departure_time: '',
    return_arrival_time: '',
    aircraft: '',
    total_seats: 0,
    jet_price: 0,
    seats_sold: 0,
    client_price: 0,
    breakeven_seats: 0,
    early_bird_price: 0,
    group_pricing: {},
    status: 'Planifié',
    active: true,
    departure: '',
    arrival: '',
    included_services: [],
    thumbnail_image: ''
  });
  
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  
  const [availableServices] = useState([
    'Boissons dont Champagne à bord',
    'Repas - Collations – Sandwichs – Viennoiseries...',
    'Transfert ville jusqu\'à aéroport',
    'WiFi haute vitesse',
    'Service de conciergerie (suivant niveau carte)',
    'Presse',
    'Salons privés',
    'Parking aéroport'
  ]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEdit);

  useEffect(() => {
    if (isEdit) {
      fetchFlight();
    }
  }, [flightId]);

  const fetchFlight = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/flights`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const flights = await response.json();
        const flight = flights.find(f => f.id === flightId);
        if (flight) {
          setFormData({
            flight_number: flight.flight_number,
            title: flight.title || '',
            date: flight.date,
            departure_time: flight.departure_time || '',
            arrival_time: flight.arrival_time || '',
            duration: flight.duration || '',
            trip_type: flight.trip_type || 'Aller simple',
            return_date: flight.return_date || '',
            return_departure_time: flight.return_departure_time || '',
            return_arrival_time: flight.return_arrival_time || '',
            aircraft: flight.aircraft,
            total_seats: flight.total_seats,
            jet_price: flight.jet_price,
            seats_sold: flight.seats_sold,
            client_price: flight.client_price,
            breakeven_seats: flight.breakeven_seats || 0,
            early_bird_price: flight.early_bird_price || 0,
            group_pricing: flight.group_pricing || {},
            status: flight.status,
            active: flight.active,
            departure: flight.departure,
            arrival: flight.arrival,
            included_services: flight.included_services || [],
            thumbnail_image: flight.thumbnail_image || ''
          });
          if (flight.thumbnail_image) {
            // Convertir /uploads en /api/uploads pour l'aperçu
            const previewUrl = flight.thumbnail_image.replace('/uploads/', '/api/uploads/');
            setImagePreview(`${process.env.REACT_APP_BACKEND_URL}${previewUrl}`);
          }
        }
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' && name !== 'active' ? checked : type === 'number' ? Number(value) : value
    }));
  };
  
  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      included_services: prev.included_services.includes(service)
        ? prev.included_services.filter(s => s !== service)
        : [...prev.included_services, service]
    }));
  };
  
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image');
      return;
    }
    
    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('L\'image est trop volumineuse (max 5MB)');
      return;
    }
    
    setUploadingImage(true);
    
    try {
      const token = localStorage.getItem('adminToken');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/flights/upload-image`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataUpload
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, thumbnail_image: data.url }));
        // Convertir /uploads en /api/uploads pour l'aperçu
        const previewUrl = data.url.replace('/uploads/', '/api/uploads/');
        setImagePreview(`${process.env.REACT_APP_BACKEND_URL}${previewUrl}`);
        alert('Image uploadée avec succès !');
      } else {
        const errorText = await response.text();
        console.error('Upload error:', errorText);
        alert('Erreur lors de l\'upload de l\'image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Erreur lors de l\'upload de l\'image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      const url = isEdit
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/flights/${flightId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/flights`;
      
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert(isEdit ? 'Vol mis à jour avec succès' : 'Vol créé avec succès');
        navigate('/admin/flights');
      } else {
        const error = await response.json();
        alert('Erreur: ' + (error.detail || 'Erreur inconnue'));
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <AdminLayout>
        <div style={{ padding: '32px' }}>
          <p>Chargement...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ padding: '32px' }}>
        <button
          onClick={() => navigate('/admin/flights')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '24px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-secondary)'
          }}
        >
          <ArrowLeft size={20} />
          <span className="body-regular">Retour</span>
        </button>

        <h1 className="hero-medium" style={{ marginBottom: '32px' }}>
          {isEdit ? 'Modifier le vol' : 'Ajouter un vol'}
        </h1>

        <form onSubmit={handleSubmit} style={{
          maxWidth: '800px',
          background: 'var(--bg-primary)',
          border: '1px solid var(--border-light)',
          padding: '32px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            <div>
              <label className="body-regular" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600
              }}>
                Numéro de vol *
              </label>
              <input
                type="text"
                name="flight_number"
                value={formData.flight_number}
                onChange={handleChange}
                required
                placeholder="ex: HG001"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label className="body-regular" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600
              }}>
                Titre du vol *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="ex: Vol spécial Grand Prix de Monaco"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label className="body-regular" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600
              }}>
                Image de vignette (optionnel)
              </label>
              <p className="body-small" style={{ 
                marginBottom: '12px',
                color: 'var(--text-secondary)'
              }}>
                📐 Dimensions recommandées : 1200 x 400 px (ratio 3:1) • Format : JPG ou PNG • Poids : &lt; 500 KB
              </p>
              
              {imagePreview && (
                <div style={{ marginBottom: '12px' }}>
                  <img 
                    src={imagePreview} 
                    alt="Aperçu" 
                    style={{
                      width: '100%',
                      maxWidth: '600px',
                      height: 'auto',
                      border: '1px solid var(--border-light)'
                    }}
                  />
                </div>
              )}
              
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploadingImage}
                style={{
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  cursor: uploadingImage ? 'not-allowed' : 'pointer',
                  width: '100%'
                }}
              />
              {uploadingImage && (
                <p className="body-small" style={{ marginTop: '8px', color: '#2c5f7d' }}>
                  Upload en cours...
                </p>
              )}
            </div>

            <div>
              <label className="body-regular" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600
              }}>
                Date *
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div>
              <label className="body-regular" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600
              }}>
                Type de vol *
              </label>
              <select
                name="trip_type"
                value={formData.trip_type}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  background: 'var(--bg-primary)'
                }}
              >
                <option value="Aller simple">Aller simple</option>
                <option value="Aller-retour">Aller-retour</option>
              </select>
            </div>

            <div>
              <label className="body-regular" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600
              }}>
                Heure de départ *
              </label>
              <input
                type="time"
                name="departure_time"
                value={formData.departure_time}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div>
              <label className="body-regular" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600
              }}>
                Heure d'arrivée *
              </label>
              <input
                type="time"
                name="arrival_time"
                value={formData.arrival_time}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div>
              <label className="body-regular" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600
              }}>
                Durée du vol *
              </label>
              <input
                type="text"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                required
                placeholder="ex: 1h30"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {formData.trip_type === 'Aller-retour' && (
              <>
                <div style={{ gridColumn: '1 / -1' }}>
                  <h3 className="body-regular" style={{ fontWeight: 600, marginTop: '16px', marginBottom: '16px' }}>
                    Informations du vol retour
                  </h3>
                </div>

                <div>
                  <label className="body-regular" style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 600
                  }}>
                    Date de retour *
                  </label>
                  <input
                    type="date"
                    name="return_date"
                    value={formData.return_date}
                    onChange={handleChange}
                    required={formData.trip_type === 'Aller-retour'}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--border-light)',
                      fontSize: '14px',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                <div>
                  <label className="body-regular" style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 600
                  }}>
                    Heure de départ retour *
                  </label>
                  <input
                    type="time"
                    name="return_departure_time"
                    value={formData.return_departure_time}
                    onChange={handleChange}
                    required={formData.trip_type === 'Aller-retour'}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--border-light)',
                      fontSize: '14px',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                <div>
                  <label className="body-regular" style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 600
                  }}>
                    Heure d'arrivée retour *
                  </label>
                  <input
                    type="time"
                    name="return_arrival_time"
                    value={formData.return_arrival_time}
                    onChange={handleChange}
                    required={formData.trip_type === 'Aller-retour'}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--border-light)',
                      fontSize: '14px',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>
              </>
            )}

            <div>
              <label className="body-regular" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600
              }}>
                Départ *
              </label>
              <input
                type="text"
                name="departure"
                value={formData.departure}
                onChange={handleChange}
                required
                placeholder="ex: Paris CDG"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div>
              <label className="body-regular" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600
              }}>
                Arrivée *
              </label>
              <input
                type="text"
                name="arrival"
                value={formData.arrival}
                onChange={handleChange}
                required
                placeholder="ex: Monaco"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div>
              <label className="body-regular" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600
              }}>
                Appareil *
              </label>
              <input
                type="text"
                name="aircraft"
                value={formData.aircraft}
                onChange={handleChange}
                required
                placeholder="ex: Gulfstream G650"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div>
              <label className="body-regular" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600
              }}>
                Places totales *
              </label>
              <input
                type="number"
                name="total_seats"
                value={formData.total_seats}
                onChange={handleChange}
                required
                min="1"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div>
              <label className="body-regular" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600
              }}>
                Places vendues *
              </label>
              <input
                type="number"
                name="seats_sold"
                value={formData.seats_sold}
                onChange={handleChange}
                required
                min="0"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div>
              <label className="body-regular" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600
              }}>
                Prix jet (coût) *
              </label>
              <input
                type="number"
                name="jet_price"
                value={formData.jet_price}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div>
              <label className="body-regular" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600
              }}>
                Prix client (par place) *
              </label>
              <input
                type="number"
                name="client_price"
                value={formData.client_price}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div>
              <label className="body-regular" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600
              }}>
                Statut *
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  background: 'var(--bg-primary)'
                }}
              >
                <option value="Planifié">Planifié</option>
                <option value="En cours">En cours</option>
                <option value="Terminé">Terminé</option>
                <option value="Annulé">Annulé</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', paddingTop: '28px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                  style={{ cursor: 'pointer' }}
                />
                <span className="body-regular">Vol actif</span>
              </label>
            </div>
          </div>

          {/* PHASE 2 - Configuration Pricing */}
          <div style={{ marginTop: '32px', paddingTop: '32px', borderTop: '2px solid #2c5f7d' }}>
            <h3 className="heading-3" style={{ marginBottom: '24px', color: '#2c5f7d' }}>
              ⚙️ Configuration du Pricing Intelligent
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <div>
                <label className="body-regular" style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600
                }}>
                  Seuil de rentabilité (places) *
                </label>
                <input
                  type="number"
                  name="breakeven_seats"
                  value={formData.breakeven_seats}
                  onChange={handleChange}
                  required
                  min="0"
                  max={formData.total_seats}
                  placeholder="ex: 4"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
                <p className="body-small" style={{ marginTop: '4px', color: 'var(--text-secondary)' }}>
                  Nombre de places minimum pour confirmer le vol
                </p>
              </div>

              <div>
                <label className="body-regular" style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600
                }}>
                  Prix Early Bird *
                </label>
                <input
                  type="number"
                  name="early_bird_price"
                  value={formData.early_bird_price}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  placeholder="ex: 2200"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
                <p className="body-small" style={{ marginTop: '4px', color: 'var(--text-secondary)' }}>
                  Prix réduit jusqu'au seuil
                </p>
              </div>

              <div>
                <label className="body-regular" style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600
                }}>
                  Prix Standard (après seuil) *
                </label>
                <input
                  type="number"
                  name="client_price"
                  value={formData.client_price}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
                <p className="body-small" style={{ marginTop: '4px', color: 'var(--text-secondary)' }}>
                  Prix normal après confirmation
                </p>
              </div>
            </div>

            {/* Prix dégressif groupe */}
            <div>
              <label className="body-regular" style={{
                display: 'block',
                marginBottom: '12px',
                fontWeight: 600
              }}>
                Prix dégressif par quantité (optionnel)
              </label>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(4, 1fr)', 
                gap: '12px',
                padding: '16px',
                background: 'rgba(44, 95, 125, 0.05)',
                border: '1px solid var(--border-light)'
              }}>
                {[1, 2, 3, 4].map((num) => (
                  <div key={num}>
                    <label className="body-small" style={{ 
                      display: 'block', 
                      marginBottom: '4px',
                      fontWeight: 600,
                      color: 'var(--text-secondary)'
                    }}>
                      {num} place{num > 1 ? 's' : ''}
                    </label>
                    <input
                      type="number"
                      value={formData.group_pricing[num] || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          group_pricing: {
                            ...prev.group_pricing,
                            [num]: value ? parseFloat(value) : undefined
                          }
                        }));
                      }}
                      placeholder={num === 1 ? "Prix de base" : "Prix réduit"}
                      step="0.01"
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid var(--border-light)',
                        fontSize: '13px',
                        fontFamily: 'inherit'
                      }}
                    />
                  </div>
                ))}
              </div>
              <p className="body-small" style={{ marginTop: '8px', color: 'var(--text-secondary)' }}>
                💡 Ex: 1 place = 2500€, 2 places = 2400€, 3 places = 2300€, 4+ places = 2200€
              </p>
            </div>
          </div>

          {/* Prestations incluses */}
          <div style={{ marginTop: '32px', paddingTop: '32px', borderTop: '1px solid var(--border-light)' }}>
            <label className="body-regular" style={{
              display: 'block',
              marginBottom: '16px',
              fontWeight: 600
            }}>
              Prestations incluses
            </label>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
              gap: '12px' 
            }}>
              {availableServices.map((service) => (
                <label key={service} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  padding: '8px',
                  border: '1px solid var(--border-light)',
                  background: formData.included_services.includes(service) ? 'rgba(44, 95, 125, 0.1)' : 'transparent'
                }}>
                  <input
                    type="checkbox"
                    checked={formData.included_services.includes(service)}
                    onChange={() => handleServiceToggle(service)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span className="body-small">{service}</span>
                </label>
              ))}
            </div>
          </div>

          <div style={{
            marginTop: '32px',
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={() => navigate('/admin/flights')}
              style={{
                padding: '12px 24px',
                background: 'transparent',
                border: '1px solid var(--border-light)',
                cursor: 'pointer',
                color: 'var(--text-secondary)'
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: loading ? 0.6 : 1
              }}
            >
              <Save size={20} />
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AddEditFlight;